// src/pages/agents/AgentEditor.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Button,
} from '@mui/material';
import {
  Save as SaveIcon, ArrowBack as ArrowBackIcon, 
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { agentService } from '../../api/agentService';
import { promptService } from '../../api/promptService'; // Added for fetching prompts
import AgentForm from '../../components/agents/AgentForm';

const AgentEditor = () => {
  const navigate = useNavigate();
  const { agentId } = useParams(); // agentId is the UUID from the route
  const isEditing = !!agentId; // This page is only for creation, edit is via AgentSettingsTab

  const formRef = useRef(); // To trigger form submission programmatically

  const [agentDataForForm, setAgentDataForForm] = useState(null); // For initial data if needed
  const [formKey, setFormKey] = useState(Date.now()); // To re-key and re-init form if needed

  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(true);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fetch available prompts for the dropdown
  useEffect(() => {
    if (!isEditing) { // Only fetch for create mode
      setLoadingPrompts(true);
      promptService.getAllPrompts({ unlinked_only: false }) // Fetch all prompts
        .then(res => {
          if (!res.isError && Array.isArray(res)) {
            setAvailablePrompts(res);
          } else {
            toast.error(res.message || "Failed to load prompts for selection.");
            setAvailablePrompts([]);
          }
        })
        .catch(err => {
          toast.error("Error fetching prompts.");
          console.error(err);
          setAvailablePrompts([]);
        })
        .finally(() => setLoadingPrompts(false));
    } else {
        setLoadingPrompts(false); // Not needed for editing on this page
    }
  }, [isEditing]);

  // Note: AgentEditor is now primarily for CREATING agents.
  // Editing is handled by AgentSettingsTab which uses AgentForm.
  // So, `loadAgentData` and related useEffects for editing are removed from here.

  const handleFormSubmit = async (formData) => {
    setIsSaving(true);
    setError(null);

    const { initial_prompt_id_to_link, ...agentPayload } = formData;

    const res = await agentService.createAgent(agentPayload);
    
    if (res.isError) {
      setError(res.message || 'Save failed.');
      toast.error(res.message || 'Failed to create agent.');
      if (res.status === 422 && res.originalError?.response?.data?.detail) {
        // Handle detailed validation errors from backend if necessary
      }
      setIsSaving(false);
    } else {
      const createdAgentId = res.id; // Assuming API returns created agent with ID
      toast.success('Agent created successfully.');

      if (initial_prompt_id_to_link && createdAgentId) {
        toast.info("Linking selected prompt...");
        const linkRes = await agentService.linkPromptToAgent(createdAgentId, initial_prompt_id_to_link);
        if (linkRes.isError) {
          toast.error(`Agent created, but failed to link prompt: ${linkRes.message}`);
        } else {
          toast.success("Prompt linked. Activating...");
          const activateRes = await agentService.setActivePromptForAgent(createdAgentId, initial_prompt_id_to_link);
          if (activateRes.isError) {
            toast.error(`Prompt linked, but failed to activate: ${activateRes.message}`);
          } else {
            toast.success("Prompt linked and activated successfully!");
          }
        }
      }
      setIsSaving(false);
      if (createdAgentId) {
        navigate(`/agents/${createdAgentId}/overview`);
      } else {
        navigate('/agents'); // Fallback
      }
    }
  };
  
  const triggerFormSubmit = () => {
     if (formRef.current) {
        const formElement = formRef.current.querySelector('form'); 
        if (formElement) {
            formElement.requestSubmit();
        } else {
            console.error("Could not find form element to submit in AgentForm (EditorPage).");
        }
    }
  };

  // If AgentEditor becomes purely for "Create", initial loading might not be complex
  // For now, we keep a simple loading check for prompts.
  if (loadingPrompts && !isEditing) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress /> <Typography sx={{ml:1}}>Loading prompt options...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" mb={3}>
        Create New Agent
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Box ref={formRef}>
        <AgentForm
            key={formKey} // If you implement a reset that needs to re-key
            initialData={agentDataForForm} // Pass null or default structure for creation
            isEditing={false} // Explicitly false for creation
            onSubmit={handleFormSubmit}
            isSaving={isSaving}
            availablePrompts={availablePrompts} // Pass fetched prompts
            loadingPrompts={loadingPrompts}     // Pass loading state
        />
      </Box>

      <Box mt={3} display="flex" justifyContent="space-between">
        <Button
          onClick={() => navigate('/agents')}
          startIcon={<ArrowBackIcon />}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={triggerFormSubmit}
          disabled={isSaving || loadingPrompts}
          startIcon={isSaving ? <CircularProgress size={22} color="inherit" /> : <SaveIcon />}
        >
          {isSaving ? 'Creating...' : 'Create Agent'}
        </Button>
      </Box>
    </Paper>
  );
};

export default AgentEditor;